// VoiceCloning.jsx — Phase 3A Voice Cloning Wizard
// Step-by-step voice clone setup: Consent → Capture → Submit → Test

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Mic, MicOff, Play, Square, CheckCircle, AlertCircle, Loader2,
  Upload, Trash2, RefreshCw, Volume2, Shield, Clock, ChevronRight,
  ChevronLeft, Sparkles, Info, CheckCircle2, XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost, apiDel, api } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';

const STEPS = [
  { id: 'consent',  label: 'Consent',   icon: Shield },
  { id: 'capture',  label: 'Capture',   icon: Mic },
  { id: 'review',   label: 'Review',    icon: CheckCircle },
  { id: 'submit',   label: 'Submit',    icon: Upload },
  { id: 'test',     label: 'Test',      icon: Volume2 },
];

const SAMPLE_SCRIPTS = [
  "The insurance industry is built on relationships and trust. My job is to help families protect what matters most to them — their home, their health, and their financial future.",
  "When I speak with a potential client, I always start by listening. Understanding their unique situation and concerns allows me to recommend the coverage that truly fits their needs and budget.",
  "Life is unpredictable, and that's exactly why insurance exists. Whether it's a sudden illness, an accident, or an unexpected loss, having the right coverage means you can focus on recovery instead of financial stress.",
  "I've been helping families in this community for years, and every policy I write represents a promise to protect someone who trusts me. That responsibility drives everything I do.",
  "The best time to review your coverage is before you need it. Let me help you understand your options and make sure your family is protected no matter what comes your way.",
];

function formatDuration(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export default function VoiceCloning({ embedded = false } = {}) {
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [consentText, setConsentText] = useState('');
  const [agentVoiceId, setAgentVoiceId] = useState(null);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingType, setRecordingType] = useState(null); // 'consent' | 'sample'
  const [currentScriptIdx, setCurrentScriptIdx] = useState(0);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [recordingMs, setRecordingMs] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Fetch current status
  const { data: statusData, refetch: refetchStatus, isLoading } = useQuery({
    queryKey: ['voice-cloning-status'],
    queryFn: () => apiGet('/voice-cloning/me'),
    staleTime: 5000,
  });

  const status = statusData?.data || statusData || {};

  // Sync step to current status
  useEffect(() => {
    if (!status.exists) return;
    if (status.status === 'ready') setStep(4);
    else if (status.status === 'training') setStep(3);
    else if (status.status === 'capturing') setStep(1);
    else if (status.status === 'pending_consent') setStep(0);
  }, [status.status]);

  // Fetch consent text
  useEffect(() => {
    apiGet('/voice-cloning/consent-text')
      .then((r) => {
        const d = r?.data || r || {};
        setConsentText(d.consentText || '');
        if (d.agentVoiceId) setAgentVoiceId(d.agentVoiceId);
      })
      .catch(() => {});
  }, []);

  // ─── Recording helpers ────────────────────────────────────────────────────

  const startRecording = useCallback(async (type) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.start(200);
      setIsRecording(true);
      setRecordingType(type);
      setRecordingMs(0);

      timerRef.current = setInterval(() => setRecordingMs((p) => p + 200), 200);
    } catch (err) {
      toast.error('Microphone access denied. Please allow microphone access.');
    }
  }, []);

  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current) return resolve(null);
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        resolve({ blob, durationMs: recordingMs });
      };
      mediaRecorderRef.current.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      clearInterval(timerRef.current);
      setIsRecording(false);
      setRecordingType(null);
    });
  }, [recordingMs]);

  async function blobToBase64(blob) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }

  // ─── Mutations ────────────────────────────────────────────────────────────

  const consentMutation = useMutation({
    mutationFn: async () => {
      const result = await stopRecording();
      if (!result || result.durationMs < 2000) throw new Error('Recording too short. Please read the full consent text.');
      const audio = await blobToBase64(result.blob);
      return apiPost('/voice-cloning/consent', { audio });
    },
    onSuccess: () => {
      toast.success('Consent recorded!');
      refetchStatus();
      setStep(1);
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message || 'Failed to save consent'),
  });

  const sampleMutation = useMutation({
    mutationFn: async ({ blob, durationMs }) => {
      const audio = await blobToBase64(blob);
      return apiPost('/voice-cloning/sample', { audio, durationMs });
    },
    onSuccess: (r) => {
      const d = r?.data || r || {};
      toast.success(`Sample saved! ${d.sampleCount} total (${formatDuration(d.totalDurationMs || 0)} recorded)`);
      refetchStatus();
      // Advance to next script
      setCurrentScriptIdx((i) => Math.min(i + 1, SAMPLE_SCRIPTS.length - 1));
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message || 'Failed to save sample'),
  });

  const submitMutation = useMutation({
    mutationFn: () => apiPost('/voice-cloning/submit', {}),
    onSuccess: (r) => {
      const d = r?.data || r || {};
      if (d.stubMode) {
        toast.success('Voice clone created (stub mode — add ElevenLabs key for real clone)');
      } else {
        toast.success('Voice clone submitted to ElevenLabs!');
      }
      refetchStatus();
      setStep(4);
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message || 'Submit failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDel('/voice-cloning/me'),
    onSuccess: () => {
      toast.success('Voice clone deleted');
      refetchStatus();
      setStep(0);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Delete failed'),
  });

  // Test TTS
  const [testBusy, setTestBusy] = useState(false);
  const [testText, setTestText] = useState('Hello! This is a test of my AI voice clone. How does it sound?');

  async function handleTestTTS() {
    setTestBusy(true);
    try {
      const res = await api.post('/voice-cloning/test', { text: testText }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audio.play();
      toast.success('Playing your cloned voice!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'TTS test failed');
    } finally {
      setTestBusy(false);
    }
  }

  // ─── Handle capture stop + save ──────────────────────────────────────────

  async function handleStopCapture() {
    const result = await stopRecording();
    if (!result) return;
    if (result.durationMs < 3000) {
      toast.error('Recording too short. Please speak for at least 3 seconds.');
      return;
    }
    sampleMutation.mutate(result);
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {!embedded && <TopBar title="Voice Cloning" subtitle="AI Dialer" />}
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-civix-purple animate-spin" />
        </div>
      </div>
    );
  }

  const totalMs = status.totalDurationMs || 0;
  const progressPct = Math.min(100, (totalMs / 60000) * 100);

  return (
    <div className="space-y-4">
      {!embedded && (
        <TopBar
          title="Voice Cloning"
          subtitle="AI Dialer · Create your AI voice"
          actions={
            status.status === 'ready' && (
              <button onClick={() => deleteMutation.mutate()} className="btn-ghost text-red-400 text-sm">
                <Trash2 className="w-4 h-4 mr-1" /> Delete Clone
              </button>
            )
          }
        />
      )}
      {embedded && status.status === 'ready' && (
        <div className="flex justify-end">
          <button onClick={() => deleteMutation.mutate()} className="btn-ghost text-red-400 text-sm">
            <Trash2 className="w-4 h-4 mr-1" /> Delete Clone
          </button>
        </div>
      )}

      {/* Step indicator */}
      <div className="panel p-4">
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active   = i === step;
            const complete = i < step;
            return (
              <React.Fragment key={s.id}>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  active   ? 'bg-civix-purple/20 text-white border border-civix-purple/40' :
                  complete ? 'text-civix-cyan' : 'text-white/30'
                }`}>
                  {complete ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  <span className="hidden sm:block">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <ChevronRight className={`w-4 h-4 shrink-0 ${complete ? 'text-civix-cyan' : 'text-white/20'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step content */}
      <div className="panel p-6">

        {/* ── Step 0: Consent ─────────────────────────────────────────── */}
        {step === 0 && (
          <div className="max-w-2xl space-y-6">
            <div>
              <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5 text-civix-purple" />
                Voice Clone Consent
              </h2>
              <p className="text-white/60 text-sm">
                To create your AI voice clone, you must verbally read and consent to the following statement.
                This recording is required by ElevenLabs Terms of Service.
              </p>
            </div>

            <div className="bg-civix-panelLight rounded-xl p-5 border border-civix-border">
              <div className="flex items-start gap-2 mb-3">
                <Info className="w-4 h-4 text-civix-cyan shrink-0 mt-0.5" />
                <span className="text-xs text-white/50">Read this statement aloud, clearly and naturally:</span>
              </div>
              <p className="text-white/90 text-sm leading-relaxed whitespace-pre-line">{consentText}</p>
            </div>

            <div className="flex items-center gap-4">
              {!isRecording ? (
                <button
                  onClick={() => startRecording('consent')}
                  className="btn-primary flex items-center gap-2"
                >
                  <Mic className="w-4 h-4" />
                  Start Recording Consent
                </button>
              ) : (
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-red-400">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                    <span className="font-mono text-sm">{formatDuration(recordingMs)}</span>
                    <span className="text-white/50 text-sm">Recording...</span>
                  </div>
                  <button
                    onClick={() => consentMutation.mutate()}
                    disabled={consentMutation.isPending}
                    className="btn-primary flex items-center gap-2"
                  >
                    {consentMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    Stop & Save
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Step 1: Capture ─────────────────────────────────────────── */}
        {step === 1 && (
          <div className="max-w-2xl space-y-6">
            <div>
              <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Mic className="w-5 h-5 text-civix-purple" />
                Voice Samples
              </h2>
              <p className="text-white/60 text-sm">
                Record at least 30 seconds of natural speech. Read each script naturally, as if speaking to a client.
              </p>
            </div>

            {/* Progress */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/60">Total recorded: {formatDuration(totalMs)}</span>
                <span className={`text-sm font-medium ${totalMs >= 60000 ? 'text-civix-cyan' : 'text-white/60'}`}>
                  {totalMs >= 60000 ? '✓ Ready to submit' : `Need ${formatDuration(Math.max(0, 30000 - totalMs))} more`}
                </span>
              </div>
              <div className="h-2 bg-civix-border rounded-full overflow-hidden">
                <div
                  className="h-full bg-civix-gradient transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <div className="text-xs text-white/40 mt-1">{status.sampleCount || 0} samples · {formatDuration(totalMs)} total</div>
            </div>

            {/* Script selector */}
            <div className="bg-civix-panelLight rounded-xl p-5 border border-civix-border">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-white/50 uppercase tracking-wider">Script {currentScriptIdx + 1} of {SAMPLE_SCRIPTS.length}</span>
                <div className="flex gap-1">
                  {SAMPLE_SCRIPTS.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentScriptIdx(i)}
                      className={`w-6 h-6 rounded-full text-xs font-bold transition-all ${
                        i === currentScriptIdx ? 'bg-civix-purple text-white' : 'bg-civix-border text-white/40 hover:bg-civix-panelLight'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-white/90 text-sm leading-relaxed">{SAMPLE_SCRIPTS[currentScriptIdx]}</p>
            </div>

            {/* Recording controls */}
            <div className="flex items-center gap-4">
              {!isRecording ? (
                <button
                  onClick={() => startRecording('sample')}
                  disabled={sampleMutation.isPending}
                  className="btn-primary flex items-center gap-2"
                >
                  <Mic className="w-4 h-4" />
                  Record This Script
                </button>
              ) : (
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-red-400">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                    <span className="font-mono text-sm">{formatDuration(recordingMs)}</span>
                  </div>
                  <button
                    onClick={handleStopCapture}
                    disabled={sampleMutation.isPending}
                    className="btn-primary flex items-center gap-2"
                  >
                    {sampleMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
                    Stop & Save Sample
                  </button>
                </div>
              )}

              {totalMs >= 30000 && !isRecording && (
                <button
                  onClick={() => setStep(2)}
                  className="btn-secondary flex items-center gap-2"
                >
                  Review Samples
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Step 2: Review ──────────────────────────────────────────── */}
        {step === 2 && (
          <div className="max-w-2xl space-y-6">
            <div>
              <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-civix-purple" />
                Review Samples
              </h2>
              <p className="text-white/60 text-sm">
                You have {status.sampleCount || 0} samples totaling {formatDuration(totalMs)}.
                {totalMs < 30000 && ' You need at least 30 seconds total.'}
              </p>
            </div>

            <div className="space-y-3">
              {Array.from({ length: status.sampleCount || 0 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-civix-panelLight rounded-lg border border-civix-border">
                  <div className="w-8 h-8 rounded-full bg-civix-purple/20 flex items-center justify-center text-sm font-bold text-civix-purple">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white">Sample {i + 1}</div>
                    <div className="text-xs text-white/40">{formatDuration(status.agentVoice?.sampleDurationsMs?.[i] || 0)}</div>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-civix-cyan" />
                </div>
              ))}

              {(!status.sampleCount || status.sampleCount === 0) && (
                <div className="text-center py-8 text-white/40">
                  No samples yet. Go back to record some.
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="btn-ghost flex items-center gap-2">
                <ChevronLeft className="w-4 h-4" /> Add More Samples
              </button>
              {totalMs >= 30000 && (
                <button onClick={() => setStep(3)} className="btn-primary flex items-center gap-2">
                  Submit for Training <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Step 3: Submit ──────────────────────────────────────────── */}
        {step === 3 && (
          <div className="max-w-2xl space-y-6">
            <div>
              <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Upload className="w-5 h-5 text-civix-purple" />
                Create Voice Clone
              </h2>
              <p className="text-white/60 text-sm">
                Ready to submit your voice samples to ElevenLabs for cloning. This usually takes 30–60 seconds.
              </p>
            </div>

            <div className="bg-civix-panelLight rounded-xl p-5 border border-civix-border space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-civix-cyan" />
                <span className="text-sm text-white">Consent recorded</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-civix-cyan" />
                <span className="text-sm text-white">{status.sampleCount} samples ({formatDuration(totalMs)}) ready</span>
              </div>
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-civix-purple" />
                <span className="text-sm text-white">ElevenLabs Instant Voice Clone (Pro tier)</span>
              </div>
            </div>

            {status.status === 'training' ? (
              <div className="flex items-center gap-3 text-civix-cyan">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Training in progress...</span>
                <button onClick={refetchStatus} className="btn-ghost text-xs"><RefreshCw className="w-3 h-3" /></button>
              </div>
            ) : (
              <button
                onClick={() => submitMutation.mutate()}
                disabled={submitMutation.isPending}
                className="btn-primary flex items-center gap-2"
              >
                {submitMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Create My Voice Clone
              </button>
            )}
          </div>
        )}

        {/* ── Step 4: Test ────────────────────────────────────────────── */}
        {step === 4 && (
          <div className="max-w-2xl space-y-6">
            <div>
              <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-civix-purple" />
                Test Your Voice Clone
              </h2>
              <p className="text-white/60 text-sm">
                Your voice clone is ready! Enter text below and click play to hear your AI voice.
              </p>
            </div>

            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
              <div>
                <div className="text-sm font-semibold text-green-400">Voice Clone Ready</div>
                <div className="text-xs text-white/50">Voice ID: {status.voiceId}</div>
              </div>
            </div>

            <div>
              <label className="form-label">Test text</label>
              <textarea
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                rows={3}
                className="input w-full"
                placeholder="Enter text to speak..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleTestTTS}
                disabled={testBusy || !testText.trim()}
                className="btn-primary flex items-center gap-2"
              >
                {testBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
                Play My Clone
              </button>
            </div>

            <div className="pt-4 border-t border-civix-border">
              <p className="text-sm text-white/50 mb-3">
                Your voice clone is now available to use in AI Dialer campaigns.
              </p>
              <a href="/ai-dialer" className="btn-secondary text-sm inline-flex items-center gap-2">
                <ChevronRight className="w-4 h-4" /> Go to AI Dialer
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
